import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MediaViewsService {

  baseUrl = `${environment.apis.base}/media-views`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Construye los parámetros estándar para todos los endpoints:
   * - client_id (solo si lo envía el admin)
   * - dias  (alternativa a start/end)
   * - start, end (YYYY-MM-DD)
   * - group_id / reel_id / top (según aplique)
   */
  private buildParams(opts?: {
    clientId?: number;
    days?: number;
    start?: string;
    end?: string;
    groupId?: number;
    reelId?: number;
    top?: number;
  }): HttpParams {
    let params = new HttpParams();
    if (!opts) return params;

    if (typeof opts.clientId === 'number') {
      params = params.set('client_id', String(opts.clientId));
    }
    if (typeof opts.days === 'number') {
      params = params.set('dias', String(opts.days));
    } else {
      if (opts.start) params = params.set('start', opts.start);
      if (opts.end)   params = params.set('end',   opts.end);
    }
    if (typeof opts.groupId === 'number') {
      params = params.set('group_id', String(opts.groupId));
    }
    if (typeof opts.reelId === 'number') {
      params = params.set('reel_id', String(opts.reelId));
    }
    if (typeof opts.top === 'number') {
      params = params.set('top', String(opts.top));
    }
    return params;
  }

  // ===============================
  //  ENDPOINTS EXISTENTES (retrocompatibles)
  // ===============================

  /**
   * Resumen por cliente (agregado por cliente).
   * - Versión retrocompatible: acepta number (days) o un objeto de opciones.
   */
  getSummaryByClient(days?: number): Observable<any>;
  getSummaryByClient(opts?: { clientId?: number; days?: number; start?: string; end?: string }): Observable<any>;
  getSummaryByClient(arg?: number | { clientId?: number; days?: number; start?: string; end?: string }): Observable<any> {
    const opts = typeof arg === 'number' ? { days: arg } : arg;
    const url = `${this.baseUrl}/resumen-por-cliente`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }

  /**
   * Serie por fecha del cliente (para gráficas).
   * - Versión retrocompatible: acepta number (days) o un objeto.
   */
  getSummaryByDateAndClient(days?: number): Observable<any[]>;
  getSummaryByDateAndClient(opts?: { clientId?: number; days?: number; start?: string; end?: string }): Observable<any[]>;
  getSummaryByDateAndClient(arg?: number | { clientId?: number; days?: number; start?: string; end?: string }): Observable<any[]> {
    const opts = typeof arg === 'number' ? { days: arg } : arg;
    const url = `${this.baseUrl}/resumen-por-fecha`;
    const params = this.buildParams(opts);
    return this.http.get<any[]>(url, { params });
  }

  /**
   * Resumen por media dentro del cliente.
   * - Versión retrocompatible: acepta number (days) o un objeto.
   */
  getSummaryByMedia(days?: number): Observable<any>;
  getSummaryByMedia(opts?: { clientId?: number; days?: number; start?: string; end?: string }): Observable<any>;
  getSummaryByMedia(arg?: number | { clientId?: number; days?: number; start?: string; end?: string }): Observable<any> {
    const opts = typeof arg === 'number' ? { days: arg } : arg;
    const url = `${this.baseUrl}/resumen-por-media`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }

  /**
   * Resumen por cliente y media (ranking de medias del cliente).
   * - Versión retrocompatible: acepta number (days) o un objeto.
   */
  getSummaryByClientAndMedia(days?: number): Observable<any>;
  getSummaryByClientAndMedia(opts?: { clientId?: number; days?: number; start?: string; end?: string }): Observable<any>;
  getSummaryByClientAndMedia(arg?: number | { clientId?: number; days?: number; start?: string; end?: string }): Observable<any> {
    const opts = typeof arg === 'number' ? { days: arg } : arg;
    const url = `${this.baseUrl}/resumen-por-cliente-media`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }

  // ===============================
  //  NUEVOS ENDPOINTS PARA DASHBOARD
  // ===============================

  /**
   * NUEVO: Ranking por grupo del cliente.
   * Acepta filtros estándar y top opcional.
   */
  getSummaryByGroup(opts?: { clientId?: number; days?: number; start?: string; end?: string; top?: number }): Observable<any> {
    const url = `${this.baseUrl}/resumen-por-grupo`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }

  /**
   * NUEVO: Ranking por reel dentro de un grupo.
   * Requiere groupId; acepta rango y clientId si es admin.
   */
  getSummaryByReel(opts: { groupId: number; clientId?: number; days?: number; start?: string; end?: string; top?: number }): Observable<any> {
    const url = `${this.baseUrl}/resumen-por-reel`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }

  /**
   * NUEVO: Ranking por media dentro de un reel.
   * Requiere reelId; acepta rango y clientId si es admin.
   */
  getSummaryByMediaOfReel(opts: { reelId: number; clientId?: number; days?: number; start?: string; end?: string; top?: number }): Observable<any> {
    const url = `${this.baseUrl}/resumen-por-media-reel`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }

  /**
   * (Opcional) KPIs para cards del dashboard.
   * Implementa el endpoint en backend si lo quieres usar.
   */
  getKpis(opts?: { clientId?: number; days?: number; start?: string; end?: string }): Observable<any> {
    const url = `${this.baseUrl}/kpis`;
    const params = this.buildParams(opts);
    return this.http.get<any>(url, { params });
  }
}
